import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTypedSelector } from "../../../hooks/useTypedSelector";
import { useActions } from "../../../hooks/useActions";
import MuiAlert from '@mui/material/Alert';
import { Box, Button, Snackbar, Tab, Tabs } from "@mui/material";
import OtherDocumentBundling from "./OtherDocumentBundling";
import { IDocumentBundle, IEventBundle } from "../../../models/configuration/bundles/IBundles";
import { RootState } from "../../../state/store";
import { useDispatch, useSelector } from "react-redux";
import EncompassService from "../../../services/encompassService";
import ConfigDocumentHandlingDialog from "./ConfigDocumentHandlingDialog";
import ConfirmationDialog from "../../../components/shared/ConfirmDialog";
import "../../../assets/style.css";
import DisclosureTrackingMappingsDialog from "./DisclosureTrackingMappingDialog";
import { IDisclosureTrackingMapping } from "../../../models/configuration/plugin/IConfiguration";
import DocumentMappingDialog from "./DocumentMappingDialog/DocumentMappingDialog";
import { signedDocColumnsRef, unsignedDocColumnsRef } from "./configbundlingdata";
import { AlertSeverity } from "../../../constants/AlertTypes";
import { SIGNED, UNSIGNED } from "../../../constants/BundlingConstants";
import { useConfigurationSave } from "../../../hooks/useConfigurationSave";
import { DtDataGrid } from "../../../components/ui/dataGrid";
import { DtPageHeader } from "../../../components/ui/header";
import DtTabs from "../../../components/ui/tab/DtTabs";
import ConfigLoad from "../../../components/shared/config/ConfigLoad";
import { ActionType } from "../../../state/actions";
import { hasDocumentBundlingChanges, hasEventBundlingChanges } from "../../../utils/bundlingConfigChangeDetector";
import { hasXPathConfigChanges } from "../../../utils/xpathConfigChangeDetector";
import { hasPushbackConfigChanges } from "../../../utils/pushbackConfigChangeDetector";
import { hasPluginConfigChanges } from "../../../utils/pluginConfigChangeDetector";
import { consumeGlobalSaveHadChanges } from "../../../utils/globalSaveStatus";

const ConfigBundling: React.FC = () => {
    const [tabValue, setTabValue] = useState(0);

    // Reducers
    const {
        getConfigBundleDocumentData,
        getConfigBundleEventData,
        saveConfigBundleData,
        saveConfigDisclosureTrackingMapping,
        saveConfigBundleEventData } = useActions();
    const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);
    const { data, error, loading } = useTypedSelector((state) => state.configDocumentBundles);
    const { data: eventData, loading: eventLoading } = useTypedSelector((state) => state.configEventBundles);
    const pluginConfiguration = useTypedSelector((state) => state.configPlugInData.data?.Configuration);
    const persistedPluginConfiguration = useTypedSelector((state) => state.configPlugInData.persistedData?.Configuration);
    const xPathData = useTypedSelector((state) => state.xPathData.data);
    const persistedXPathData = useTypedSelector((state) => state.xPathData.persistedData);
    const pushbackData = useTypedSelector((state) => state.configPushbackDefaults.data);
    const persistedPushbackData = useTypedSelector((state) => state.configPushbackDefaults.persistedData);
    const dispatch = useDispatch();
    const [saving, setSaving] = useState<boolean>(false);
    const [editLoading, setEditLoading] = useState<boolean>(false);
    const [selectedConfigId, setSelectedConfigId] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [disclosureTrackingMappingData, setDisclosureTrackingMappingData] = useState<IDisclosureTrackingMapping | null>(null);
    const [docBundlesInitialized, setDocBundlesInitialized] = useState<boolean>(false);
    const [eventBundlesInitialized, setEventBundlesInitialized] = useState<boolean>(false);

    // State
    const [unsignedDocBundles, setUnsignedDocBundles] = useState<Array<IDocumentBundle>>([]);
    const [signedDocBundles, setSignedDocBundles] = useState<Array<IDocumentBundle>>([]);
    const [otherDocEventBundles, setOtherDocEventBundles] = useState<Array<IEventBundle>>([]);
    const [selectedBundle, setSelectedBundle] = useState<IDocumentBundle | null>(null);
    const [editingBundle, setEditingBundle] = useState<boolean>(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [currentSelectedBundle, setCurrentSelectedBundle] = useState<IDocumentBundle | null>(null);
    const [disclosureTrackingMappingDialogOpen, setDisclosureTrackingMappingsDialogOpen] = useState(false);
    const [showDocumentMapping, setShowDocumentMapping] = useState(false);

    // State Confirmations
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');

    // Refs
    const hasFetched = useRef(false);

    const getBundleKey = useCallback((bundle: IDocumentBundle) => {
        const typeKey = bundle.BundlingConfigurationType ?? "";
        const packageKey = bundle.PackageId ?? "";
        return JSON.stringify([typeKey, packageKey]);
    }, []);

    const combinedDocBundles = useMemo(() => {
        const combined = [...unsignedDocBundles, ...signedDocBundles];
        const baseOrder = data?.Configuration?.Bundling ?? [];

        if (baseOrder.length === 0) {
            return JSON.parse(JSON.stringify(combined));
        }

        const orderMap = new Map<string, number>();
        baseOrder.forEach((bundle, index) => {
            const key = getBundleKey(bundle);
            if (!orderMap.has(key)) {
                orderMap.set(key, index);
            }
        });

        const sorted = [...combined].sort((left, right) => {
            const leftKey = getBundleKey(left);
            const rightKey = getBundleKey(right);
            const leftOrder = orderMap.get(leftKey);
            const rightOrder = orderMap.get(rightKey);
            if (leftOrder === undefined && rightOrder === undefined) {
                return leftKey.localeCompare(rightKey);
            }
            if (leftOrder === undefined) return 1;
            if (rightOrder === undefined) return -1;
            if (leftOrder !== rightOrder) return leftOrder - rightOrder;
            return leftKey.localeCompare(rightKey);
        });

        return JSON.parse(JSON.stringify(sorted));
    }, [data?.Configuration?.Bundling, getBundleKey, signedDocBundles, unsignedDocBundles]);

    const fetchBundles = useCallback((configId: string) => {
        getConfigBundleDocumentData(configId, accessToken);
        getConfigBundleEventData(configId, accessToken);
    }, [getConfigBundleDocumentData, getConfigBundleEventData, accessToken]);

    useEffect(() => {

        if (!hasFetched.current) {
            const configId = EncompassService.getConfigId() as string;
            setSelectedConfigId(configId);
            fetchBundles(configId);
            hasFetched.current = true;
        }
    }, [fetchBundles]);

    useEffect(() => {
        if (data != null) {
            setUnsignedDocBundles(
                JSON.parse(JSON.stringify(data.Configuration.Bundling.filter(
                    (x) => x.BundlingConfigurationType?.toLowerCase() === UNSIGNED
                )))
            );
            setSignedDocBundles(
                JSON.parse(JSON.stringify(data.Configuration.Bundling.filter(
                    (x) => x.BundlingConfigurationType?.toLowerCase() === SIGNED
                )))
            );
            setDocBundlesInitialized(true);
        } else {
            setDocBundlesInitialized(false);
        }
    }, [data]);

    useEffect(() => {
        if (!docBundlesInitialized || !data?.Configuration?.Bundling) return;
        const hasChanges = hasDocumentBundlingChanges(
            { Configuration: { Bundling: combinedDocBundles } },
            data
        );
        if (!hasChanges) {
            return;
        }
        dispatch({
            type: ActionType.GET_CONFIG_BUNDLE_DATA_SUCCESS,
            payload: { Configuration: { Bundling: combinedDocBundles } },
            fromServer: false
        });
    }, [combinedDocBundles, data, dispatch, docBundlesInitialized]);

    const eventBundles = useMemo(() => (
        eventData?.Configuration?.Bundling ? JSON.parse(JSON.stringify(eventData.Configuration.Bundling)) : []
    ), [eventData?.Configuration?.Bundling]);

    useEffect(() => {
        if (eventData?.Configuration?.Bundling) {
            setOtherDocEventBundles(eventBundles);
            setEventBundlesInitialized(true);
        } else {
            setEventBundlesInitialized(false);
        }
    }, [eventBundles, eventData?.Configuration?.Bundling]);

    const eventBundlesToSync = useMemo(() => (
        JSON.parse(JSON.stringify(otherDocEventBundles))
    ), [otherDocEventBundles]);

    useEffect(() => {
        if (!eventBundlesInitialized || !eventData?.Configuration?.Bundling) return;
        const hasChanges = hasEventBundlingChanges(
            { Configuration: { Bundling: eventBundlesToSync } },
            eventData
        );
        if (!hasChanges) {
            return;
        }
        dispatch({
            type: ActionType.GET_CONFIG_BUNDLE_EVENT_DATA_SUCCESS,
            payload: { Configuration: { Bundling: eventBundlesToSync } },
            fromServer: false
        });
    }, [dispatch, eventBundlesInitialized, eventBundlesToSync, eventData]);

    const updateBundleArrays = useCallback(() => {
        if (selectedBundle) {
            const bundleType = selectedBundle.BundlingConfigurationType?.toLowerCase();
            const packageId = selectedBundle.PackageId;

            if (!packageId) return;

            if (bundleType === UNSIGNED) {
                setUnsignedDocBundles(prevBundles => {
                    const updatedBundles = prevBundles.map(bundle => 
                        bundle.PackageId === packageId ? selectedBundle : bundle
                    );
                    return updatedBundles;
                });
            } else {
                setSignedDocBundles(prevBundles => {
                    const updatedBundles = prevBundles.map(bundle => 
                        bundle.PackageId === packageId ? selectedBundle : bundle
                    );
                    return updatedBundles;
                });
            }
        }
    }, [selectedBundle]);

    const handleDialogClose = useCallback(() => {
        updateBundleArrays();
        setEditingBundle(false);
    }, [updateBundleArrays]);

    function editItem(item: any) {
        setSelectedBundle(item);
        setEditingBundle(true);
    }

    function deleteBundle(item: any) {
        let docindex = unsignedDocBundles.findIndex(bundle => bundle.PackageId === item.PackageId);
        if (docindex > -1) {
            setUnsignedDocBundles([
                ...unsignedDocBundles.slice(0, docindex),
                ...unsignedDocBundles.slice(docindex + 1),
            ]);
        } else {
            docindex = signedDocBundles.findIndex(bundle => bundle.PackageId === item.PackageId);
            if (docindex > -1) {
                setSignedDocBundles([
                    ...signedDocBundles.slice(0, docindex),
                    ...signedDocBundles.slice(docindex + 1),
                ]);
            }
        }

        setCurrentSelectedBundle(null);
        setConfirmDialogOpen(false);
    }

    const updateOtherDocumentEventBundles = (eventBundles: Array<IEventBundle>) => {
        setOtherDocEventBundles(eventBundles);
    };

    function cancelDeleteBundle() {
        setConfirmDialogOpen(false);
        setCurrentSelectedBundle(null);
    }

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const saveBundles = async () => {
        return await saveConfigBundleData(selectedConfigId, combinedDocBundles, accessToken);
    }

    const saveDisclosureTrackingMapping = async () => {
        if (!disclosureTrackingMappingData) {
            return false;
        }
        const deepCopyData = JSON.parse(JSON.stringify(disclosureTrackingMappingData)); // Deep copy
        return await saveConfigDisclosureTrackingMapping(selectedConfigId, deepCopyData, accessToken);
    }

    const saveOtherDocEventBundles = async () => {
        const deepCopyBundles = JSON.parse(JSON.stringify(otherDocEventBundles)); // Deep copy
        return await saveConfigBundleEventData(selectedConfigId, deepCopyBundles, accessToken);
    }

    const save = async () => {
        const globalSaved = consumeGlobalSaveHadChanges();
        try {
            setSaving(true);
            const results = await Promise.allSettled([
                saveBundles(),
                saveDisclosureTrackingMapping(),
                saveOtherDocEventBundles()
            ]);

            const failureMessages = results
                .filter((result) => result.status === "rejected")
                .map((result) => {
                    const reason = (result as PromiseRejectedResult).reason;
                    return reason instanceof Error ? reason.message : String(reason);
                });

            const didSaveAny = results.some(
                (result) => result.status === "fulfilled" && Boolean(result.value)
            );

            const hasPluginChanges = pluginConfiguration
                ? hasPluginConfigChanges(pluginConfiguration, persistedPluginConfiguration)
                : false;
            const hasXPathChanges = xPathData
                ? hasXPathConfigChanges(xPathData, persistedXPathData)
                : false;
            const hasPushbackChanges = pushbackData
                ? hasPushbackConfigChanges(pushbackData, persistedPushbackData)
                : false;

            const shouldShowSuccess = didSaveAny || hasPluginChanges || hasXPathChanges || hasPushbackChanges || globalSaved;

            if (failureMessages.length > 0) {
                const errorMessage = `Failed to save: ${failureMessages.join("; ")}`;
                throw new Error(errorMessage);
            }

            openAlert(shouldShowSuccess ? "Successfully Saved Changes." : "No new changes to save", shouldShowSuccess ? "success" : "info");
            setErrorMessage('');


        } catch (e) {
            console.error("Error saving data", e);
            const message = e instanceof Error ? `Failed to save data: ${e.message}` : 'Failed to save data: Unknown error';
            setErrorMessage(message);
            openAlert(message, 'error');
        } finally {
            setSaving(false);
        }
    }

    // Register save function for header buttons
    useConfigurationSave(() => save());

    // Alert] Handling 
    const handleSnackBarClose = () => setAlertOpen(false);
    const openAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
        setAlertSeverity(severity);
        setAlertMessage(message);
        setAlertOpen(true);
    };
    function openDisclosureTrackingMappings() { setDisclosureTrackingMappingsDialogOpen(true); }
    function closeDisclosureTrackingMappings() { setDisclosureTrackingMappingsDialogOpen(false); }
    function updateDisclosureTrackingMappingData(data: IDisclosureTrackingMapping | null) {
        if (!data) return;
        const deepCopyData = JSON.parse(JSON.stringify(data)) as IDisclosureTrackingMapping;
        setDisclosureTrackingMappingData(deepCopyData);

        if (!pluginConfiguration) return;

        const updatedConfig = {
            ...pluginConfiguration,
            DisclosureTrackingMapping: deepCopyData
        };
        dispatch({
            type: ActionType.SAVE_CONFIG_PLUGIN_DATA_SUCCESS,
            payload: { Configuration: updatedConfig }
        });
    }

    return (
        <>
            <section>
                 <div>
                    <div>
                        <ConfigLoad loading={loading || saving || editLoading} />
                    </div>
                </div>
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        overflow: "auto",
                        height: "100%",
                        position: "relative",
                    }}
                >
                    <DtPageHeader actions={<>
                        <Button
                            onClick={() => { setShowDocumentMapping(true); }}
                            color={'secondary'}
                            variant='text'
                            size='small'
                            sx={{ fontSize: '0.95rem'}}
                        >
                            Document Mappings
                        </Button>
                        <DisclosureTrackingMappingsDialog
                            open={disclosureTrackingMappingDialogOpen}
                            closeFxtn={closeDisclosureTrackingMappings}
                            updateData={updateDisclosureTrackingMappingData}
                        />

                        <Button
                            onClick={openDisclosureTrackingMappings}
                            color={'secondary'}
                            variant='text'
                            size='small'
                            sx={{ fontSize: '0.95rem'}}
                        >
                            Disclosure Tracking Mappings
                        </Button>
                    </>}>Document Bundling</DtPageHeader>

                    <DtTabs
                        tabs={[
                            { label: `Unsigned Documents (${loading ? '...' : unsignedDocBundles.length})` },
                            { label: `Signed Documents (${loading ? '...' : signedDocBundles.length})` },
                            { label: `Other Documents (${eventLoading ? '...' : otherDocEventBundles.length})` }
                        ]}
                        value={tabValue}
                        onChange={handleTabChange}
                    >
                        {/* Unsigned Documents */}
                        <DtDataGrid
                            data={unsignedDocBundles}
                            columns={unsignedDocColumnsRef()}
                            tableName="unsigneddocs"
                            paged={true}
                            responsivePageSize={true}
                            responsivePageConfig={{heightRatio: 0.75}}
                            editConfig={{
                                mode: 'custom',
                                onEdit: (row: any) => editItem(row)
                            }}
                            deleteConfig={{
                                confirm: true,
                                confirmTitle: "Delete Bundling",
                                confirmMessage: "Are you sure you want to delete the selected Bundling?",
                                confirmYesLabel: "Delete",
                                confirmNoLabel: "Cancel",
                                onDelete: (row: any) => deleteBundle(row)
                            }}
                        />

                        {/* Signed Documents */}
                        <DtDataGrid
                            data={signedDocBundles}
                            columns={signedDocColumnsRef()}
                            tableName="signeddocs"
                            paged={true}
                            responsivePageSize={true}
                            responsivePageConfig={{heightRatio: 0.75}}
                            dense={false}
                            editConfig={{
                                mode: 'custom',
                                onEdit: (row: any) => editItem(row)
                            }}
                            deleteConfig={{
                                confirm: true,
                                confirmTitle: "Delete Bundling",
                                confirmMessage: "Are you sure you want to delete the selected Bundling?",
                                confirmYesLabel: "Delete",
                                confirmNoLabel: "Cancel",
                                onDelete: (row: any) => deleteBundle(row)
                            }}
                        />

                        {/* Other Documents */}
                        <OtherDocumentBundling
                            updateOtherDocuments={updateOtherDocumentEventBundles}
                            docBundles={data}
                            initialEventBundles={otherDocEventBundles}
                            loading={eventLoading}
                            error={error ?? undefined}
                        />

                    </DtTabs>

                </Box>
            </section>
            {selectedBundle && editingBundle}
            {
                <ConfigDocumentHandlingDialog
                    open={editingBundle}
                    documentBundle={selectedBundle!}
                    updateBundle={setSelectedBundle}
                    closeFxtn={handleDialogClose}
                />
            }

            <ConfirmationDialog
                open={confirmDialogOpen}
                title="Delete Configuration"
                cancelName="No"
                confirmName="Yes"
                message={`Are you sure you want to delete the  '${currentSelectedBundle?.BundlingConfigurationType}' Bundling Configuration for '${currentSelectedBundle?.PackageId}'?`}
                onConfirm={() => deleteBundle(currentSelectedBundle)}
                onCancel={() => cancelDeleteBundle()}
            />

            <DocumentMappingDialog
                open={showDocumentMapping}
                closeFxtn={() => setShowDocumentMapping(false)}
            />

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={alertOpen}
                autoHideDuration={4000}
                onClose={handleSnackBarClose}
            >
                <MuiAlert
                    onClose={handleSnackBarClose}
                    severity={alertSeverity}
                    sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
                    {alertMessage}
                </MuiAlert>
            </Snackbar>
        </>
    );
};

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function DocsTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`bundle-tabpanel-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 1, height: 1 }}>{children}</Box>}
        </div>
    );
}

function docPanelProps(index: number) {
    return {
        id: `bundle-tab-${index}`,
    };
}

export default ConfigBundling;